import { ApiOutlined } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { ResourceRule } from "@p0security/shared/types/workflow/types";
import { Flex, Typography } from "antd";
import {
  PermissionIntegrationLogo,
  hasLogo,
} from "components/Integrations/IntegrationLogo";
import {
  RoutingRulePopover,
  StyledRoutingRulePreview,
} from "components/Jit/Routing/styles";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { capitalize } from "lodash";
import pluralize from "pluralize";
import { useMemo } from "react";

import { useInstalledIntegrations } from "../../providers/InstalledIntegrationsProvider";
import { ResourceFilterCount } from "./ResourceFilterCount";

export type ResourceRulePreview =
  | ResourceRule
  | {
      type: "genericIntegration";
    };

type Props = {
  resource: ResourceRulePreview;
};

export const ResourcePreview: React.FC<Props> = ({ resource }) => {
  const { integrationMap, installedResources } = useInstalledIntegrations();

  const resourceFilters = useMemo(() => {
    if (resource.type !== "integration") {
      return [];
    }

    // Must be after the resource.type check above
    const { filters } = resource;

    if (!filters) {
      return [];
    }

    return Object.keys(filters).map((key) => ({
      filterType: key,
      filter: filters[key as keyof typeof filters],
    }));
  }, [resource]);

  return resource.type === "genericIntegration" ? (
    <StyledRoutingRulePreview>
      <ApiOutlined />
      <span>Resource integration</span>
    </StyledRoutingRulePreview>
  ) : resource.type === "any" ? (
    <RoutingRulePopover
      text="Any installed resource"
      icon={<ApiOutlined />}
      popoverContent={
        <Flex>
          {installedResources.length > 0 && (
            <Flex vertical gap={4}>
              {installedResources.map((i) => {
                return (
                  <Flex align="center" key={i.label} gap={8}>
                    {hasLogo(i.key) && PermissionIntegrationLogo[i.key]}
                    <Typography.Text>{i.label}</Typography.Text>
                  </Flex>
                );
              })}
            </Flex>
          )}
          <TenantAwareLink to={AppPaths.Integrations}>
            Manage integrations
          </TenantAwareLink>
        </Flex>
      }
    />
  ) : (
    <StyledRoutingRulePreview>
      <div className="icon-container">
        {PermissionIntegrationLogo[resource.service]}
      </div>
      <Typography.Paragraph className="label">
        {(integrationMap.find((i) => i.key === resource.service)?.label ??
          capitalize(resource.service)) +
          // display the access type in addition to the integration name, if applicable
          // e.g. "Gcloud: Roles"
          (resource.type === "integration" &&
          resource.accessType &&
          resource.accessType !== "any"
            ? `: ${capitalize(pluralize(resource.accessType))}`
            : "")}
      </Typography.Paragraph>

      {resourceFilters.length > 0 && (
        <ResourceFilterCount resourceFilters={resourceFilters} />
      )}
    </StyledRoutingRulePreview>
  );
};
